/**
 * Competition Committee voting.
 *
 * The thresholds are spread across three rules and nobody remembers them correctly in the room,
 * which is how the league ended up with five different rules in 2016 and 2018 that all said
 * "passed with only six votes and must be re-voted next year" in slightly different words.
 *
 * What the rulebook actually says:
 *
 *   2015-I-1  A quorum of EIGHT members must be represented — physically, virtually or by proxy —
 *             before a meeting can begin. Seven "yes" votes is the super-majority and makes a
 *             rule permanent. If only 8 or 9 are accounted for, the requirement is relaxed: six
 *             "yes" votes put the rule in effect for the coming season ONLY, and it must be
 *             re-voted the following year. An abstention is a vote against.
 *
 *   2019-I-2  A rule up for re-vote must reach the super-majority to survive; six is no longer
 *             enough the second time. Modifying the substance of a proposal does not make it a
 *             new rule — it still needs a super-majority.
 *
 *   2020-I-1  If any of the original proposing GMs does not support a modification, it cannot
 *             move forward even with a super-majority.
 *
 * The relaxation is conditional on ATTENDANCE, not on the vote count: six yes with all ten
 * present is a failure, not a provisional pass. That is the detail this file exists to get right.
 */

export const LEAGUE_SIZE = 10;
export const QUORUM = 8;
export const SUPER_MAJORITY = 7;
export const RELAXED_MAJORITY = 6;

export type Vote = 'yes' | 'no' | 'abstain';

export type ProposalOutcome = 'passed' | 'provisional' | 'failed' | 'no-quorum';

export interface VoteTally {
  yes: number;
  no: number;
  abstain: number;
  /** GMs represented at the meeting, by any means. Drives the relaxed threshold. */
  represented: number;
}

export interface VoteResult {
  outcome: ProposalOutcome;
  /** One sentence, in the language the minutes use. */
  summary: string;
  /** True when the rule goes on the following year's agenda automatically. */
  requiresRevote: boolean;
  /** The rule ids behind the decision, for the citation chips. */
  citations: string[];
}

export interface VoteContext {
  /** True when this proposal is itself the mandatory re-vote of a provisional pass. */
  isRevote?: boolean;
  /**
   * 2020-I-1 §2: the proposal was modified in the meeting and one of the original proposers is
   * against the modification. That vetoes it regardless of the count.
   */
  proposerObjects?: boolean;
}

export function tallyOutcome(t: VoteTally, ctx: VoteContext = {}): VoteResult {
  const cites = ['2015-I-1'];

  if (t.represented < QUORUM) {
    return {
      outcome: 'no-quorum',
      summary: `Only ${t.represented} of ${LEAGUE_SIZE} represented — a quorum of ${QUORUM} is `
        + 'required before the committee can vote at all.',
      requiresRevote: false,
      citations: cites,
    };
  }

  if (ctx.proposerObjects) {
    return {
      outcome: 'failed',
      summary: 'An original proposer does not support the modification, so it cannot move '
        + 'forward whatever the count.',
      requiresRevote: false,
      citations: [...cites, '2020-I-1'],
    };
  }

  // An abstention is a vote against, by league precedent recorded in 2015-I-1.
  if (t.yes >= SUPER_MAJORITY) {
    return {
      outcome: 'passed',
      summary: `${t.yes} yes — super-majority reached. Permanent, and not subject to re-vote.`,
      requiresRevote: false,
      citations: cites,
    };
  }

  if (ctx.isRevote) {
    return {
      outcome: 'failed',
      summary: `${t.yes} yes on a re-vote. A rule up for re-vote needs the super-majority of `
        + `${SUPER_MAJORITY} to continue; it lapses.`,
      requiresRevote: false,
      citations: [...cites, '2019-I-2'],
    };
  }

  if (t.yes >= RELAXED_MAJORITY && t.represented < LEAGUE_SIZE) {
    return {
      outcome: 'provisional',
      summary: `${t.yes} yes with ${t.represented} represented — in effect for the coming season `
        + 'only, and on next year’s agenda for a mandatory re-vote.',
      requiresRevote: true,
      citations: [...cites, '2019-I-2'],
    };
  }

  if (t.yes >= RELAXED_MAJORITY) {
    return {
      outcome: 'failed',
      summary: `${t.yes} yes with all ${LEAGUE_SIZE} represented. The relaxed threshold of `
        + `${RELAXED_MAJORITY} only applies when 8 or 9 are accounted for, so this needed `
        + `${SUPER_MAJORITY}.`,
      requiresRevote: false,
      citations: cites,
    };
  }

  return {
    outcome: 'failed',
    summary: `${t.yes} yes, ${t.no} no, ${t.abstain} abstaining — short of the ${SUPER_MAJORITY} `
      + 'required. Abstentions count against.',
    requiresRevote: false,
    citations: cites,
  };
}

/** Roll a per-GM vote map up into a tally. Anyone not represented is simply absent. */
export function tallyFrom(
  votes: Readonly<Record<string, Vote>>,
  represented: number,
): VoteTally {
  const vals = Object.values(votes);
  return {
    yes: vals.filter((v) => v === 'yes').length,
    no: vals.filter((v) => v === 'no').length,
    abstain: vals.filter((v) => v === 'abstain').length,
    represented,
  };
}

/** What the vote needs from here, for the live readout while the room is still voting. */
export function votesStillNeeded(t: VoteTally, ctx: VoteContext = {}): string {
  if (t.represented < QUORUM) return `${QUORUM - t.represented} more represented for a quorum`;
  const short = SUPER_MAJORITY - t.yes;
  if (short <= 0) return 'super-majority reached';
  if (!ctx.isRevote && t.represented < LEAGUE_SIZE && t.yes >= RELAXED_MAJORITY) {
    return `${short} more for a permanent pass — provisional as it stands`;
  }
  return `${short} more yes for a permanent pass`;
}

export const OUTCOME_LABEL: Readonly<Record<ProposalOutcome, string>> = {
  passed: 'Passed',
  provisional: 'Provisional — re-vote next year',
  failed: 'Failed',
  'no-quorum': 'No quorum',
};

/* -- the docket, and branching ---------------------------------------------------------------- */

/**
 * AN AMENDMENT BRANCHED IN THE ROOM.
 *
 * The league does this every year and the app could not record it. Item 3 fails 2–7, somebody
 * says "it would pass without the roster cut", the wording is changed on the spot and voted
 * again. Recorded as two unrelated proposals, the minutes read as though the committee voted on
 * defenses twice and nobody can reconstruct why the second one passed.
 *
 * So a branch keeps its lineage: it sits under the item it came from, takes that item's number
 * with a letter, and INHERITS THE ORIGINAL PROPOSERS. The inheritance is not bookkeeping — it is
 * what gives the man who wrote the first version his 2020-I-1 veto over a rewording he did not
 * agree to. A branch that forgot its parents would quietly strip somebody of a right.
 *
 * A branch is otherwise A NEW PROPOSAL: it is not a re-vote even when its parent was, so the
 * super-majority makes it permanent. That is the one thing easiest to get backwards, and the
 * drawing spells it out on the readout.
 */
export interface BranchInput {
  parent: {
    id: string;
    season: number;
    gm: string;
    proposers?: readonly string[];
    amends?: readonly string[];
    title: string;
  };
  /** Who is adding the amendment. He joins the proposers; he does not replace them. */
  by: string;
  title: string;
  body: string;
  now: string;
}

export interface BranchDraft {
  season: number;
  gm: string;
  proposers: string[];
  branchedFrom: string;
  title: string;
  body: string;
  amends: string[];
  status: 'docketed';
  isRevote: false;
  createdAt: string;
  updatedAt: string;
}

export function branchFrom(input: BranchInput): BranchDraft {
  const { parent, by, now } = input;
  const inherited = (parent.proposers?.length ? parent.proposers : [parent.gm])
    .map((g) => g.toUpperCase());
  const proposers = inherited.includes(by.toUpperCase())
    ? inherited
    : [...inherited, by.toUpperCase()];

  return {
    season: parent.season,
    // The author of record stays the ORIGINAL author. The branch is his proposal amended, not
    // somebody else's new one, and the veto follows the same logic.
    gm: parent.gm,
    proposers,
    branchedFrom: parent.id,
    title: input.title,
    body: input.body,
    amends: [...(parent.amends ?? [])],
    status: 'docketed',
    /*
     * NOT a re-vote, whatever the parent was. A re-vote is the second look at a rule that already
     * passed provisionally; a branch is a fresh proposal that happens to share a subject. Carrying
     * the flag across would hold a new idea to a threshold it never earned.
     */
    isRevote: false,
    createdAt: now,
    updatedAt: now,
  };
}

/** Everyone whose agreement the 2020-I-1 veto belongs to. */
export const proposersOf = (p: { gm: string; proposers?: readonly string[] }): string[] =>
  (p.proposers?.length ? [...p.proposers] : [p.gm]).map((g) => g.toUpperCase());

/**
 * The number each docket item is called in the minutes — `1`, `2`, `3`, `3a`, `4`.
 *
 * Branches do not consume an integer. That is the whole point of the lettering: the minutes read
 * "3 failed, 3a passed", which is one argument with two votes in it, rather than "3 failed, 4
 * passed", which reads as two unrelated decisions and loses the reason the second one succeeded.
 *
 * A branch of a branch keeps going down the alphabet from its own parent's number (`3a` → `3aa`
 * is deliberately not what happens; it becomes `3b`, a sibling), because two rewordings of one
 * item are siblings in the room whatever order somebody typed them in.
 */
export function docketNumbers(
  order: readonly string[],
  byId: ReadonlyMap<string, { branchedFrom?: string }>,
): Map<string, string> {
  const out = new Map<string, string>();
  const letters = new Map<string, number>();
  let n = 0;

  for (const id of order) {
    const parent = byId.get(id)?.branchedFrom;
    if (parent) {
      // Branch of a branch is a sibling of that branch, not a grandchild — see above.
      const root = rootOf(id, byId);
      const base = out.get(root);
      if (base) {
        const seq = (letters.get(root) ?? 0);
        letters.set(root, seq + 1);
        out.set(id, `${base}${String.fromCharCode(97 + seq)}`);
        continue;
      }
      // Orphaned branch — the parent is not on this docket. It is still a real item, so it takes
      // its own number rather than vanishing.
    }
    n += 1;
    out.set(id, String(n));
  }
  return out;
}

function rootOf(id: string, byId: ReadonlyMap<string, { branchedFrom?: string }>): string {
  const seen = new Set<string>();
  let at = id;
  for (;;) {
    const parent = byId.get(at)?.branchedFrom;
    if (!parent || seen.has(parent)) return at;
    seen.add(parent);
    at = parent;
  }
}

/**
 * The sentence under the item's title, which is the whole reason this screen exists.
 *
 * Five thresholds interact and nobody recalls them correctly in a room at nine in the evening.
 * The screen never shows a bare count: it says what THIS item needs before anybody votes, and the
 * readout afterwards says what the count means. Written here rather than in JSX so the wording
 * is the same on the docket, on the voting screen and in the minutes.
 */
export function thresholdNote(p: { isRevote?: boolean; branchedFrom?: string }): string {
  if (p.isRevote) {
    return `A re-vote needs the full super-majority of ${SUPER_MAJORITY}. The relaxed `
      + `${RELAXED_MAJORITY} does not apply the second time, whatever the attendance.`;
  }
  if (p.branchedFrom) {
    return `A branch is a new proposal, so ${SUPER_MAJORITY} makes it permanent — and `
      + `${RELAXED_MAJORITY} with someone absent puts it in for one season only.`;
  }
  return `${SUPER_MAJORITY} yes makes it permanent. ${RELAXED_MAJORITY} passes it for one season `
    + 'only, and then only if somebody is not represented.';
}

/**
 * What the record button will WRITE, so it never says a neutral "Save".
 *
 * A button that says Save on the one screen where getting the threshold wrong has consequences
 * is a button that gets pressed without reading the readout above it. This makes the outcome the
 * label, so pressing it is an act of agreement rather than of dismissal.
 */
export function recordLabel(outcome: ProposalOutcome): string {
  return outcome === 'passed' ? 'Record as passed'
    : outcome === 'provisional' ? 'Record as provisional'
      : outcome === 'no-quorum' ? 'Record — no quorum'
        : 'Record as failed';
}

/**
 * The proposals one manager needs to see under "Your proposals".
 *
 * HIS OWN, AND THE ONES HE TYPED FOR SOMEBODY ELSE. The second half is not a nicety - without it
 * a commissioner transcribing a proposal on another GM's behalf and pressing "Hold as a draft"
 * writes a record that appears on no screen in the app. It is not his (`gm` is the author, and
 * has to be, because 2020-I-1 §2 hangs the veto off it), it is not submitted so it is not in the
 * commissioners' inbox, and a draft is never on the docket. Tim lost one this way on 25 August.
 *
 * The author still sees it too - it is his proposal, and it shows in his list the moment he logs
 * in. Both men need it: the one it belongs to, and the one who has to remember to submit it.
 */
export function proposalsFor<T extends { gm: string; enteredBy?: string }>(
  all: readonly T[], me: string,
): T[] {
  const up = me.toUpperCase();
  return all.filter((p) => p.gm.toUpperCase() === up || p.enteredBy?.toUpperCase() === up);
}

/* -- what is actually on the docket ------------------------------------------------------------ */

/**
 * A PROPOSAL IS NOT A DOCKET ITEM UNTIL IT REACHES THE COMMITTEE.
 *
 * Home and the deadline rail both counted `proposals.length` — every row in the collection,
 * including drafts nobody has sent and proposals since withdrawn. Tim, 31 August: "docket is
 * complete (there were only 3 items ... )" against a card reading `On the docket 4`.
 *
 * The four statuses below are the ones that have been put to the room: still to be voted on, or
 * already answered. A `draft` is one manager's private notes and a `withdrawn` one was taken back
 * before the vote; neither is something the committee sits down to.
 */
export const ON_DOCKET: readonly string[] = ['docketed', 'passed', 'provisional', 'failed'];

export const onDocket = (ps: ReadonlyArray<{ status: string }>): number =>
  ps.filter((p) => ON_DOCKET.includes(p.status)).length;

/** Still waiting for the committee's answer. Zero means the night's work is done. */
export const undecided = (ps: ReadonlyArray<{ status: string }>): number =>
  ps.filter((p) => p.status === 'docketed').length;
